import { AdBanner } from "@/components/AdBanner";
import { AuthDialog } from "@/components/AuthDialog";
import { Comments } from "@/components/Comments";
import { HelpRequestButton } from "@/components/HelpRequestButton";
import { ShareKakaoButton } from "@/components/ShareKakaoButton";
import { PageContainer } from "@/components/layout/PageContainer";
import { NewsCard } from "@/components/NewsCard";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { normalizeImageUrl } from "@/lib/image-url";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { canUseFeature, incrementUsage } from "@/lib/userService";
import { Article, categories } from "@shared/schema";
import { useMutation, useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { ko } from "date-fns/locale";
import {
  ArrowLeft,
  Bookmark,
  BookmarkCheck,
  BookmarkPlus,
  BookOpen,
  Brain,
  Calendar,
  ExternalLink,
  Languages,
  Loader2,
  Tag,
  User,
  Volume2,
  VolumeX,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useParams } from "wouter";

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const { user, userProfile, isAuthenticated, isPremium } = useAuth();
  const { data: article, isLoading } = useQuery<Article>({
    queryKey: [`/api/articles/${id}`],
  });

  const { data: relatedArticles } = useQuery<Article[]>({
    queryKey: ["/api/articles"],
    enabled: !!article,
  });

  const [language, setLanguage] = useState<"ko" | "en" | "dual">("ko");
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [selectedWord, setSelectedWord] = useState<string>("");
  const [wordDefinition, setWordDefinition] = useState<any>(null);
  const [isLoadingDefinition, setIsLoadingDefinition] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [simpleVersion, setSimpleVersion] = useState<any>(null);
  const [isLoadingSimple, setIsLoadingSimple] = useState(false);
  const [showSimple, setShowSimple] = useState(false);
  const [quiz, setQuiz] = useState<any>(null);
  const [isLoadingQuiz, setIsLoadingQuiz] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState<string[]>([]);
  const [showQuizResults, setShowQuizResults] = useState(false);
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [hoveredButton, setHoveredButton] = useState<string | null>(null);
  const [currentEncouragement, setCurrentEncouragement] = useState("");
  const { toast } = useToast();
  const contentRef = useRef<HTMLDivElement>(null);

  const learningTips = {
    english:
      "영어 원문으로 읽으면 자연스러운 표현과 문맥을 익힐 수 있어요. 모르는 단어는 더블클릭해서 저장하세요!",
    tts: "음성으로 듣기는 발음과 억양을 배울 수 있는 최고의 방법입니다. 듣고 따라 읽어보세요!",
    simple:
      "쉬운 영어로 변환하면 A2-B1 수준의 단순한 문장으로 내용을 이해할 수 있어요. 초급자에게 완벽!",
    quiz: "퀴즈로 이해도를 테스트하세요! 틀린 문제는 다시 기사를 읽으며 복습하는 기회가 됩니다.",
    vocabulary:
      "단어장에서 저장한 단어들을 정기적으로 복습하세요. 반복이 기억의 핵심입니다!",
  };

  const encouragementMessages = [
    "🌟 꾸준히 하시는 모습 멋져요! 오늘도 영어 실력이 한 단계 올라갑니다!",
    "💪 영어 학습의 비결은 꾸준함입니다. 지금처럼만 계속하세요!",
    "🎯 매일 조금씩, 여러분은 이미 영어 마스터를 향해 가고 있어요!",
    "✨ 작은 노력이 모여 큰 변화를 만듭니다. 파이팅!",
    "🚀 포기하지 않는 여러분, 정말 대단해요! 계속 도전하세요!",
    "📚 독서만큼 좋은 영어 공부는 없습니다. 계속 읽어보세요!",
    "🎓 실수는 성장의 증거입니다. 부끄러워하지 마세요!",
    "🔥 어제보다 나은 오늘의 나! 영어 학습 화이팅!",
  ];

  const getRandomEncouragement = () => {
    return encouragementMessages[
      Math.floor(Math.random() * encouragementMessages.length)
    ];
  };

  // Scroll to top when article changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // SEO Meta Tags + Canonical Link
  useEffect(() => {
    if (article) {
      // Set document title
      document.title = `${article.titleKo || article.title} | OZK News`;

      // Set canonical link
      const canonicalUrl = `https://ozknews.com/article/${article.id}`;
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.setAttribute("rel", "canonical");
        document.head.appendChild(canonical);
      }
      canonical.setAttribute("href", canonicalUrl);

      // Set meta description
      const metaDesc =
        language === "ko"
          ? article.metaDescriptionKo ||
            article.summaryKo ||
            article.summary ||
            ""
          : article.metaDescription ||
            article.summary ||
            article.summaryKo ||
            "";

      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement("meta");
        metaDescription.setAttribute("name", "description");
        document.head.appendChild(metaDescription);
      }
      if (metaDesc) {
        metaDescription.setAttribute("content", metaDesc.substring(0, 160));
      }

      // Set meta keywords
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (!metaKeywords) {
        metaKeywords = document.createElement("meta");
        metaKeywords.setAttribute("name", "keywords");
        document.head.appendChild(metaKeywords);
      }
      const keywords = [
        ...(article.focusKeywords || []),
        ...(article.tags || []),
      ].join(", ");
      metaKeywords.setAttribute("content", keywords);

      // Open Graph tags
      const setOgTag = (property: string, content: string) => {
        let tag = document.querySelector(`meta[property="${property}"]`);
        if (!tag) {
          tag = document.createElement("meta");
          tag.setAttribute("property", property);
          document.head.appendChild(tag);
        }
        tag.setAttribute("content", content);
      };

      setOgTag("og:title", article.titleKo || article.title);
      if (metaDesc) {
        setOgTag("og:description", metaDesc.substring(0, 160));
      }
      setOgTag("og:type", "article");
      setOgTag("og:url", window.location.href);
      if (article.imageUrl) {
        setOgTag("og:image", article.imageUrl);
      }

      // Twitter Card tags
      const setTwitterTag = (name: string, content: string) => {
        let tag = document.querySelector(`meta[name="${name}"]`);
        if (!tag) {
          tag = document.createElement("meta");
          tag.setAttribute("name", name);
          document.head.appendChild(tag);
        }
        tag.setAttribute("content", content);
      };

      setTwitterTag("twitter:card", "summary_large_image");
      setTwitterTag("twitter:title", article.titleKo || article.title);
      if (metaDesc) {
        setTwitterTag("twitter:description", metaDesc.substring(0, 160));
      }
      if (article.imageUrl) {
        setTwitterTag("twitter:image", article.imageUrl);
      }
    }
  }, [article, language]);

  // Debug: Log article imageUrl when article loads
  useEffect(() => {
    if (article) {
      console.log("Article loaded:", {
        id: article.id,
        title: article.title,
        imageUrl: article.imageUrl,
        hasImageUrl: !!article.imageUrl,
      });
    }
  }, [article]);

  // Initialize encouragement message on mount
  useEffect(() => {
    setCurrentEncouragement(getRandomEncouragement());
  }, []);

  // Update encouragement when mouse leaves a button
  const handleMouseLeave = () => {
    setHoveredButton(null);
    setCurrentEncouragement(getRandomEncouragement());
  };

  // 찜하기 상태 확인
  const { data: bookmarkData } = useQuery<{
    isBookmarked: boolean;
    bookmarkId?: string;
  }>({
    queryKey: ["/api/bookmarks/check", user?.uid, "article", article?.id],
    // The default queryFn only fetches queryKey[0] and ignores the rest of
    // the key, so this always resolved "/api/bookmarks/check" with no
    // params - a custom queryFn is required to actually pass userId/itemType/
    // itemId through (same class of bug fixed for MarketRegister.tsx's
    // edit-mode query).
    queryFn: async () => {
      const params = new URLSearchParams({
        userId: user?.uid || "",
        itemType: "article",
        itemId: String(article?.id ?? ""),
      });
      const res = await fetch(`/api/bookmarks/check?${params.toString()}`);
      if (!res.ok) throw new Error("Failed to check bookmark");
      return res.json();
    },
    enabled: isAuthenticated && !!user && !!article,
  });

  const isBookmarked = bookmarkData?.isBookmarked || false;

  // 찜하기 추가
  const addBookmarkMutation = useMutation({
    mutationFn: async () => {
      if (!user || !article) throw new Error("로그인이 필요합니다");
      return await apiRequest("POST", "/api/bookmarks", {
        userId: user.uid,
        itemType: "article",
        itemId: article.id,
        itemTitle: article.titleKo || article.title,
        itemImageUrl: article.imageUrl || null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/bookmarks/check", user?.uid, "article", article?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/bookmarks", user?.uid],
      });
      toast({
        title: "찜 완료",
        description: "마이페이지에서 확인하실 수 있습니다",
      });
    },
    onError: (error: any) => {
      toast({
        title: "찜하기 실패",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // 찜하기 삭제
  const removeBookmarkMutation = useMutation({
    mutationFn: async () => {
      if (!bookmarkData?.bookmarkId)
        throw new Error("북마크 ID를 찾을 수 없습니다");
      return await apiRequest(
        "DELETE",
        `/api/bookmarks/${bookmarkData.bookmarkId}`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/bookmarks/check", user?.uid, "article", article?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/bookmarks", user?.uid],
      });
      toast({
        title: "찜하기 취소",
        description: "찜하기가 해제되었습니다",
      });
    },
    onError: (error: any) => {
      toast({
        title: "찜하기 취소 실패",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleBookmark = () => {
    if (!isAuthenticated) {
      setShowAuthDialog(true);
      return;
    }

    if (isBookmarked) {
      removeBookmarkMutation.mutate();
    } else {
      addBookmarkMutation.mutate();
    }
  };

  const isBookmarking =
    addBookmarkMutation.isPending || removeBookmarkMutation.isPending;

  // TTS (Text-to-Speech) functionality
  const handleSpeak = () => {
    if (!article) return;

    // Stop if already speaking
    if (isSpeaking) {
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
      setIsSpeaking(false);
      return;
    }

    // Check if speech synthesis is available
    if (!window.speechSynthesis) {
      toast({
        title: "음성 재생 불가",
        description: "이 브라우저는 음성 재생을 지원하지 않습니다.",
        variant: "destructive",
      });
      return;
    }

    const textToSpeak =
      language === "en"
        ? `${article.title || ""}. ${article.summary || ""}. ${
            article.content || ""
          }`
        : `${article.titleKo || ""}. ${article.summaryKo || ""}. ${
            article.contentKo || ""
          }`;

    if (!textToSpeak.trim()) {
      toast({
        title: "음성 재생 불가",
        description: "읽을 내용이 없습니다.",
        variant: "destructive",
      });
      return;
    }

    // Cancel any ongoing speech
    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(textToSpeak);
    utterance.lang = language === "en" ? "en-US" : "ko-KR";
    utterance.rate = 0.9;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;

    utterance.onend = () => {
      setIsSpeaking(false);
    };

    utterance.onerror = (event) => {
      console.error("Speech synthesis error:", event);
      setIsSpeaking(false);
      toast({
        title: "음성 재생 오류",
        description:
          "음성 재생 중 오류가 발생했습니다. 브라우저를 확인해주세요.",
        variant: "destructive",
      });
    };

    setIsSpeaking(true);

    try {
      window.speechSynthesis.speak(utterance);
    } catch (error) {
      console.error("Error starting speech:", error);
      setIsSpeaking(false);
      toast({
        title: "음성 재생 오류",
        description: "음성 재생을 시작할 수 없습니다.",
        variant: "destructive",
      });
    }
  };

  // Save word to Firestore with definition
  const handleWordSelection = async (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    // Get the word at the double-click position
    const selection = window.getSelection();
    let selectedText = selection?.toString().trim();

    // If no selection or multiple words, try to get word from click position
    if (!selectedText || selectedText.split(/\s+/).length !== 1) {
      try {
        // Use document.caretRangeFromPoint (Chrome, Safari) or caretPositionFromPoint (Firefox)
        const range =
          (document as any).caretRangeFromPoint?.(e.clientX, e.clientY) ||
          (document as any).caretPositionFromPoint?.(e.clientX, e.clientY);

        if (range) {
          const textNode = range.startContainer || range.offsetNode;
          if (textNode && textNode.nodeType === Node.TEXT_NODE) {
            const text = textNode.textContent || "";
            const offset = range.startOffset || range.offset || 0;

            // Find word boundaries around the cursor position
            let start = offset;
            let end = offset;

            // Move start backward to find word start
            while (start > 0 && /[\w'-]/.test(text[start - 1])) {
              start--;
            }

            // Move end forward to find word end
            while (end < text.length && /[\w'-]/.test(text[end])) {
              end++;
            }

            if (start < end) {
              selectedText = text.substring(start, end).trim();
            }
          }
        }
      } catch (error) {
        console.warn("Error extracting word from click position:", error);
      }
    }

    // Validate: must be a single word with only letters, numbers, hyphens, and apostrophes
    if (
      selectedText &&
      selectedText.split(/\s+/).length === 1 &&
      /^[\w'-]+$/i.test(selectedText)
    ) {
      // Check authentication and limits
      if (!isAuthenticated) {
        toast({
          title: "로그인이 필요합니다",
          description: "단어를 저장하려면 먼저 로그인해주세요.",
          variant: "destructive",
        });
        return;
      }

      if (!userProfile) return;

      // Check free tier limit (premium and platinum users have unlimited)
      if (
        userProfile.role !== "premium" &&
        userProfile.role !== "platinum" &&
        userProfile.role !== "admin"
      ) {
        // Check vocabulary count via backend API
        try {
          const vocabResponse = await apiRequest(
            "GET",
            `/api/vocabulary/${userProfile.uid}`
          );
          const vocabWords = ((await vocabResponse.json()) as any[]) || [];

          if (vocabWords.length >= 20) {
            toast({
              title: "저장 한도 초과",
              description:
                "무료 회원은 최대 20개의 단어만 저장할 수 있습니다. 프리미엄으로 업그레이드하여 무제한으로 저장하세요.",
              variant: "destructive",
            });
            return;
          }
        } catch (error) {
          console.error("Error checking vocabulary count:", error);
        }
      }

      try {
        // Get word definition first
        const defResponse = await apiRequest("POST", "/api/word-definition", {
          word: selectedText,
        });
        const definition = (await defResponse.json()) as {
          definition?: string;
          pronunciation?: string;
          example?: string;
          korean?: string;
          synonyms?: string[];
          antonyms?: string[];
        };

        // Save via backend API
        await apiRequest("POST", "/api/vocabulary/save", {
          userId: userProfile.uid,
          word: selectedText,
          definition: (definition as any)?.definition || "",
          pronunciation: (definition as any)?.pronunciation || "",
          exampleSentence: (definition as any)?.example || "",
          koreanTranslation: (definition as any)?.korean || "",
          synonyms: (definition as any)?.synonyms || [],
          antonyms: (definition as any)?.antonyms || [],
          articleId: article?.id || "",
          articleTitle:
            language === "en" ? article?.title : article?.titleKo || "",
          proficiency: "unknown",
        });

        toast({
          title: "단어 저장됨",
          description: `"${selectedText}" 단어를 단어장에 저장했습니다.`,
        });
      } catch (error) {
        console.error("Error saving word:", error);
        toast({
          title: "저장 실패",
          description: "단어 저장 중 오류가 발생했습니다.",
          variant: "destructive",
        });
      }
    }
  };

  // Fetch word definition
  const fetchWordDefinition = async (word: string) => {
    // Check authentication
    if (!isAuthenticated) {
      toast({
        title: "로그인이 필요합니다",
        description: "단어 뜻을 보려면 먼저 로그인해주세요.",
        variant: "destructive",
      });
      setPopoverOpen(false);
      return;
    }

    if (!userProfile) return;

    // Check free tier limit
    if (!canUseFeature(userProfile, "wordLookup")) {
      toast({
        title: "일일 한도 초과",
        description:
          "무료 회원은 하루에 5개의 단어만 조회할 수 있습니다. 프리미엄으로 업그레이드하여 무제한으로 사용하세요.",
        variant: "destructive",
      });
      setPopoverOpen(false);
      return;
    }

    setIsLoadingDefinition(true);
    try {
      const response = await apiRequest("POST", "/api/word-definition", {
        word,
      });
      const result = (await response.json()) as {
        definition?: string;
        pronunciation?: string;
        example?: string;
        korean?: string;
        synonyms?: string[];
        antonyms?: string[];
      };
      setWordDefinition(result);

      // Increment usage counter
      if (userProfile) {
        await incrementUsage(userProfile.uid, "wordLookup");
      }
    } catch (error) {
      toast({
        title: "오류",
        description: "단어 뜻을 가져오는 중 오류가 발생했습니다.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingDefinition(false);
    }
  };

  // Handle word click
  const handleWordClick = (event: React.MouseEvent) => {
    if (language !== "en") return;

    const target = event.target as HTMLElement;
    const word = target.textContent?.trim();

    if (word && word.split(/\s+/).length === 1 && /^[a-zA-Z]+$/.test(word)) {
      setSelectedWord(word);
      setPopoverOpen(true);
      fetchWordDefinition(word);
    }
  };

  // Fetch simple version
  const fetchSimpleVersion = async () => {
    if (!article) return;

    // Check if article has English content
    if (!article.content && !article.title) {
      toast({
        title: "오류",
        description: "영어 콘텐츠가 없습니다.",
        variant: "destructive",
      });
      return;
    }

    setIsLoadingSimple(true);
    try {
      // article.id가 문자열이면 숫자로 변환 시도
      const articleId =
        typeof article.id === "string" ? parseInt(article.id, 10) : article.id;

      const response = await apiRequest("POST", "/api/simplify-article", {
        title: article.title || "",
        content: article.content || "",
        articleId: isNaN(Number(articleId)) ? undefined : articleId, // 데이터베이스에서 먼저 확인하기 위해 전송
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result = (await response.json()) as {
        simpleTitle?: string;
        simpleContent?: string;
        error?: string;
        warning?: string;
      };

      // The server returns a graceful fallback (original title/content) when
      // AI is disabled/unavailable or the OpenAI call itself fails - in both
      // cases simpleTitle/simpleContent are still populated. Previously any
      // truthy result.error unconditionally threw here, discarding that
      // fallback and showing a generic error toast even though the server
      // had already produced something displayable. Only throw when there's
      // no usable fallback content at all.
      if (result.error && !result.simpleTitle && !result.simpleContent) {
        throw new Error(result.error);
      }

      setSimpleVersion({
        simpleTitle: result.simpleTitle || article.title,
        simpleContent: result.simpleContent || article.content,
      });
      setShowSimple(true);

      if (result.error || result.warning) {
        console.warn(
          "[ArticleDetail] Simplify article returned with fallback content:",
          result.error || result.warning,
        );
      }
    } catch (error: any) {
      console.error("Error simplifying article:", error);
      toast({
        title: "오류",
        description:
          error.message || "쉬운 영어 버전 생성 중 오류가 발생했습니다.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingSimple(false);
    }
  };

  // Fetch quiz
  const fetchQuiz = async () => {
    if (!article) return;

    // Check authentication
    if (!isAuthenticated) {
      toast({
        title: "로그인이 필요합니다",
        description: "퀴즈를 풀려면 먼저 로그인해주세요.",
        variant: "destructive",
      });
      return;
    }

    if (!userProfile) return;

    // Check free tier limit
    if (!canUseFeature(userProfile, "quiz")) {
      toast({
        title: "주간 한도 초과",
        description:
          "무료 회원은 주 1회만 퀴즈를 풀 수 있습니다. 프리미엄으로 업그레이드하여 무제한으로 사용하세요.",
        variant: "destructive",
      });
      return;
    }

    // Check if article has English content
    if (!article.content && !article.title) {
      toast({
        title: "오류",
        description: "영어 콘텐츠가 없습니다.",
        variant: "destructive",
      });
      return;
    }

    setIsLoadingQuiz(true);
    try {
      // article.id가 문자열이면 숫자로 변환 시도
      const articleId =
        typeof article.id === "string" ? parseInt(article.id, 10) : article.id;

      const response = await apiRequest("POST", "/api/generate-quiz", {
        title: article.title || "",
        content: article.content || "",
        articleId: isNaN(Number(articleId)) ? undefined : articleId, // 데이터베이스에서 먼저 확인하기 위해 전송
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result = (await response.json()) as {
        questions?: Array<{
          question: string;
          options: string[];
          correct: string;
          explanation?: string;
        }>;
        error?: string;
      };

      if (result.error) {
        // API 키가 없을 때 특별한 메시지
        if (result.error.includes("API key not configured")) {
          throw new Error("서비스 설정 오류입니다. 관리자에게 문의해주세요.");
        }
        throw new Error(result.error);
      }

      if (!result.questions || result.questions.length === 0) {
        throw new Error("퀴즈 문제를 생성할 수 없습니다.");
      }

      setQuiz(result);
      setShowQuiz(true);
      setQuizAnswers(new Array(result.questions.length).fill(""));
      setShowQuizResults(false);

      // Increment usage counter
      if (userProfile) {
        await incrementUsage(userProfile.uid, "quiz");
      }
    } catch (error: any) {
      console.error("Error generating quiz:", error);
      toast({
        title: "오류",
        description: error.message || "퀴즈 생성 중 오류가 발생했습니다.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingQuiz(false);
    }
  };

  // Submit quiz answers
  const submitQuiz = () => {
    setShowQuizResults(true);
    const correctCount = (
      quiz as { questions: { correct: string }[] }
    ).questions.filter(
      (q: any, i: number) => quizAnswers[i] === q.correct
    ).length;

    toast({
      title: "퀴즈 완료!",
      description: `${
        (quiz as { questions: { correct: string }[] }).questions.length
      }문제 중 ${correctCount}문제 정답입니다.`,
    });
  };

  // Function to split content into paragraphs
  const splitIntoParagraphs = (text: string): string[] => {
    return text.split(/\n\n+/).filter((p) => p.trim().length > 0);
  };

  // Render dual language content
  const renderDualContent = () => {
    const englishParagraphs = splitIntoParagraphs(article?.content || "");
    const koreanParagraphs = splitIntoParagraphs(article?.contentKo || "");

    const maxLength = Math.max(
      englishParagraphs.length,
      koreanParagraphs.length
    );

    return (
      <div className="space-y-6">
        {Array.from({ length: maxLength }).map((_, index) => (
          <div
            key={index}
            className="space-y-3 pb-4 border-b border-border last:border-0"
          >
            {englishParagraphs[index] && (
              <p className="text-base leading-relaxed text-primary">
                {englishParagraphs[index]}
              </p>
            )}
            {koreanParagraphs[index] && (
              <p className="text-base leading-relaxed text-muted-foreground">
                {koreanParagraphs[index]}
              </p>
            )}
          </div>
        ))}
      </div>
    );
  };

  // Clean up speech synthesis on unmount
  useEffect(() => {
    return () => {
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-bold mb-4">기사를 찾을 수 없습니다</h1>
        <Link href="/">
          <Button>홈으로 돌아가기</Button>
        </Link>
      </div>
    );
  }

  const category = categories.find((c) => c.id === article.category);
  const categoryColor =
    {
      australia: "bg-chart-2 text-white",
      korea: "bg-chart-3 text-white",
      community: "bg-chart-4 text-white",
      business: "bg-chart-5 text-white",
      culture: "bg-primary text-primary-foreground",
    }[article.category] || "bg-muted text-muted-foreground";

  const related =
    relatedArticles
      ?.filter((a) => a.id !== article.id && a.category === article.category)
      .slice(0, 3) || [];

  // Generate SEO meta information
  const pageTitle = `${article.titleKo || article.title} | OZK News`;
  const pageDescription = (
    language === "ko"
      ? article.metaDescriptionKo || article.summaryKo || article.summary || ""
      : article.metaDescription || article.summary || article.summaryKo || ""
  ).substring(0, 160);
  const pageUrl = `https://ozknews.com/article/${article.id}`;
  const ogImage = article.imageUrl || "https://ozknews.com/og-image.jpg";

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={ogImage} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content={pageTitle} />
        <meta property="twitter:description" content={pageDescription} />
        <meta property="twitter:image" content={ogImage} />

        {/* Additional meta tags */}
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={pageUrl} />

        {/* Article specific meta */}
        {article.author && <meta name="author" content={article.author} />}
        {article.publishedAt && (
          <meta
            property="article:published_time"
            content={new Date(article.publishedAt).toISOString()}
          />
        )}
        {article.updatedAt && (
          <meta
            property="article:modified_time"
            content={new Date(article.updatedAt).toISOString()}
          />
        )}
        {article.category && (
          <meta property="article:section" content={article.category} />
        )}
        {article.tags && article.tags.length > 0 && (
          <>
            {article.tags.map((tag, idx) => (
              <meta key={idx} property="article:tag" content={tag} />
            ))}
          </>
        )}

        {/* Language alternates */}
        <link rel="alternate" hrefLang="ko" href={pageUrl} />
        <link rel="alternate" hrefLang="en" href={pageUrl} />
        <link rel="alternate" hrefLang="x-default" href={pageUrl} />

        {/* Keywords meta */}
        {article.focusKeywords && article.focusKeywords.length > 0 && (
          <meta name="keywords" content={article.focusKeywords.join(", ")} />
        )}
      </Helmet>

      {/* Structured Data (JSON-LD) */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "NewsArticle",
          "headline": article.titleKo || article.title,
          "description": pageDescription,
          "image": ogImage,
          "datePublished": article.publishedAt ? new Date(article.publishedAt).toISOString() : undefined,
          "dateModified": article.updatedAt ? new Date(article.updatedAt).toISOString() : article.publishedAt ? new Date(article.publishedAt).toISOString() : undefined,
          "author": article.author && !article.author.includes("편집팀") ? {
            "@type": "Person",
            "name": article.author
          } : {
            "@type": "Organization",
            "name": article.author || "OZK 뉴스 편집팀",
            "url": "https://ozknews.com"
          },
          "publisher": {
            "@type": "Organization",
            "name": "OZK News",
            "logo": {
              "@type": "ImageObject",
              "url": "https://ozknews.com/logo.png"
            }
          },
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": pageUrl
          },
          "articleSection": article.category,
          "keywords": article.focusKeywords?.join(", ") || article.tags?.join(", ") || "",
          "inLanguage": language === "ko" ? "ko" : "en",
          ...(article.imageUrl && {
            "image": {
              "@type": "ImageObject",
              "url": article.imageUrl,
              "description": article.imageDescription || article.titleKo || article.title
            }
          })
        })}
      </script>

      <PageContainer size="wide">
        <Link href="/news">
          <div>
            <Button variant="ghost" className="mb-6" data-testid="button-back">
              <ArrowLeft className="h-4 w-4 mr-2" />
              목록으로
            </Button>
          </div>
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <article className="lg:col-span-2 space-y-6">
            {/* Category Badge */}
            <Badge
              className={`${categoryColor} no-default-hover-elevate no-default-active-elevate`}
            >
              {category?.label || article.category}
            </Badge>

            {/* Non-member Banner */}
            {!isAuthenticated && (
              <Card className="border-primary/50 bg-primary/5">
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                      <h3 className="font-semibold text-primary mb-1">
                        회원가입 시 영어 학습 콘텐츠 제공
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        단어 뜻 보기, 퀴즈, 쉬운 영어 변환 등 다양한 학습 기능을
                        무료로 이용하세요
                      </p>
                    </div>
                    <Button
                      onClick={() => setShowAuthDialog(true)}
                      data-testid="button-signup-prompt"
                      className="shrink-0"
                    >
                      회원가입/로그인
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Learning Tools */}
            <div className="space-y-3">
              {/* Learning Tip Box */}
              <Card className="bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
                <CardContent className="p-3">
                  <p className="text-sm text-foreground transition-all duration-300">
                    {hoveredButton === null ? (
                      <span className="flex items-center gap-2">
                        <span className="text-base">💡</span>
                        <span>{currentEncouragement}</span>
                      </span>
                    ) : hoveredButton === "english" ? (
                      <span className="flex items-center gap-2">
                        <Languages className="h-4 w-4 text-primary" />
                        <span>{learningTips.english}</span>
                      </span>
                    ) : hoveredButton === "tts" ? (
                      <span className="flex items-center gap-2">
                        <Volume2 className="h-4 w-4 text-primary" />
                        <span>{learningTips.tts}</span>
                      </span>
                    ) : hoveredButton === "simple" ? (
                      <span className="flex items-center gap-2">
                        <BookOpen className="h-4 w-4 text-primary" />
                        <span>{learningTips.simple}</span>
                      </span>
                    ) : hoveredButton === "quiz" ? (
                      <span className="flex items-center gap-2">
                        <Brain className="h-4 w-4 text-primary" />
                        <span>{learningTips.quiz}</span>
                      </span>
                    ) : hoveredButton === "vocabulary" ? (
                      <span className="flex items-center gap-2">
                        <BookmarkPlus className="h-4 w-4 text-primary" />
                        <span>{learningTips.vocabulary}</span>
                      </span>
                    ) : null}
                  </p>
                </CardContent>
              </Card>

              {/* Learning Buttons */}
              <TooltipProvider>
                <div className="flex flex-wrap items-center gap-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const newLang =
                            language === "ko"
                              ? "en"
                              : language === "en"
                              ? "dual"
                              : "ko";
                          setLanguage(newLang);
                          setShowSimple(false);
                          setShowQuiz(false);
                        }}
                        onMouseEnter={() => setHoveredButton("english")}
                        onMouseLeave={handleMouseLeave}
                        data-testid="button-language-toggle"
                        className="gap-2"
                      >
                        <Languages className="h-4 w-4" />
                        {language === "ko"
                          ? "English"
                          : language === "en"
                          ? "한글"
                          : "동시"}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>언어 전환 (한글 → 영어 → 동시표시)</p>
                    </TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleSpeak}
                        onMouseEnter={() => setHoveredButton("tts")}
                        onMouseLeave={handleMouseLeave}
                        data-testid="button-tts"
                        className="gap-2"
                      >
                        {isSpeaking ? (
                          <VolumeX className="h-4 w-4" />
                        ) : (
                          <Volume2 className="h-4 w-4" />
                        )}
                        {isSpeaking ? "정지" : "음성 듣기"}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>기사를 음성으로 읽어줍니다</p>
                    </TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={fetchSimpleVersion}
                        onMouseEnter={() => setHoveredButton("simple")}
                        onMouseLeave={handleMouseLeave}
                        disabled={isLoadingSimple || language !== "en"}
                        data-testid="button-simple"
                        className="gap-2"
                      >
                        {isLoadingSimple ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <BookOpen className="h-4 w-4" />
                        )}
                        쉬운 영어
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        {language !== "en"
                          ? "영어 모드에서 사용 가능"
                          : "A2-B1 수준의 쉬운 영어로 변환"}
                      </p>
                    </TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={fetchQuiz}
                        onMouseEnter={() => setHoveredButton("quiz")}
                        onMouseLeave={handleMouseLeave}
                        disabled={isLoadingQuiz || language !== "en"}
                        data-testid="button-quiz"
                        className="gap-2"
                      >
                        {isLoadingQuiz ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Brain className="h-4 w-4" />
                        )}
                        퀴즈
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        {language !== "en"
                          ? "영어 모드에서 사용 가능"
                          : "이해도를 확인하는 5문제 퀴즈 생성"}
                      </p>
                    </TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link href="/vocabulary">
                        <Button
                          variant="outline"
                          size="sm"
                          onMouseEnter={() => setHoveredButton("vocabulary")}
                          onMouseLeave={handleMouseLeave}
                          data-testid="link-vocabulary"
                          className="gap-2"
                        >
                          <BookmarkPlus className="h-4 w-4" />내 단어장
                        </Button>
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>저장한 단어를 확인하고 복습하세요</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </TooltipProvider>
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight">
              {language === "dual" ? (
                <div className="space-y-2">
                  <div className="text-primary">
                    {article.title || article.titleKo}
                  </div>
                  <div className="text-2xl md:text-3xl lg:text-4xl text-muted-foreground">
                    {article.titleKo || article.title}
                  </div>
                </div>
              ) : language === "ko" ? (
                article.titleKo || article.title
              ) : (
                article.title || article.titleKo
              )}
            </h1>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground pb-4 border-b border-border">
              {(article.publishedAt || article.createdAt) && (
                <div className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  <span>
                    {(() => {
                      try {
                        const dateStr =
                          article.publishedAt || article.createdAt;
                        const date = new Date(dateStr);
                        if (isNaN(date.getTime())) {
                          return "날짜 미정";
                        }
                        return format(date, "PPP", { locale: ko });
                      } catch {
                        return "날짜 미정";
                      }
                    })()}
                  </span>
                </div>
              )}
              {article.author && (
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  <span>{article.author} 기자</span>
                  {isAuthenticated && (
                    <Button
                      variant={isBookmarked ? "default" : "outline"}
                      size="sm"
                      onClick={handleBookmark}
                      disabled={isBookmarking}
                      className="gap-1.5 ml-2"
                      data-testid="button-bookmark"
                    >
                      {isBookmarking ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : isBookmarked ? (
                        <BookmarkCheck className="h-4 w-4" />
                      ) : (
                        <Bookmark className="h-4 w-4" />
                      )}
                      <span>{isBookmarked ? "찜 완료" : "찜하기"}</span>
                    </Button>
                  )}
                  <HelpRequestButton
                    sourceType="article"
                    sourceId={article?.id ? String(article.id) : undefined}
                    className="ml-2"
                    size="sm"
                  />
                  <ShareKakaoButton
                    path={`/article/${article.id}`}
                    contentType="article"
                    contentId={article?.id ? String(article.id) : undefined}
                    title={article.titleKo || article.title}
                    description={article.summaryKo || article.summary || undefined}
                    imageUrl={article.imageUrl || undefined}
                    className="ml-2"
                    size="sm"
                  />
                </div>
              )}
            </div>

            {/* Hero Image */}
            {article.imageUrl ? (
              <div className="space-y-2">
                <div className="aspect-video md:aspect-[21/9] overflow-hidden rounded-md bg-muted relative">
                  <img
                    src={normalizeImageUrl(article.imageUrl) || ""}
                    alt={article.imageDescription || article.titleKo || article.title || "Article image"}
                    className="w-full h-full object-cover"
                    data-testid="img-article-hero"
                    onError={(e) => {
                      const normalizedUrl = normalizeImageUrl(article.imageUrl);
                      console.error("[ArticleDetail] Failed to load image:", {
                        original: article.imageUrl,
                        normalized: normalizedUrl,
                        currentSrc: e.currentTarget.currentSrc,
                      });
                      // Show placeholder on error
                      e.currentTarget.src =
                        'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="400"%3E%3Crect fill="%23e5e7eb" width="800" height="400"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="24" dy="200" dx="350"%3ENo Image%3C/text%3E%3C/svg%3E';
                    }}
                    onLoad={() => {
                      console.log("[ArticleDetail] Image loaded successfully:", {
                        original: article.imageUrl,
                        normalized: normalizeImageUrl(article.imageUrl),
                      });
                    }}
                  />
                </div>
                {/* Image Source Attribution */}
                {article.imageSource && (
                  <p className="text-xs text-muted-foreground text-right">
                    Image source: {article.imageSource === "unsplash" ? "Unsplash" :
                                   article.imageSource === "pexels" ? "Pexels" :
                                   article.imageSource === "r2" ? "OZK News" :
                                   article.imageSource}
                  </p>
                )}
              </div>
            ) : null}

            {/* Tags */}
            {article.tags && article.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <Link key={tag} href={`/tag/${tag}`}>
                    <div>
                      <Badge
                        variant="outline"
                        className="hover-elevate cursor-pointer"
                      >
                        <Tag className="h-3 w-3 mr-1" />
                        {tag}
                      </Badge>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {/* Ad 1: Top Banner (Guest/Free/Premium only) */}
            {userProfile?.role !== "platinum" && (
              <div data-testid="ad-banner-top">
                <AdBanner size="header" tags={article?.tags ?? undefined} />
              </div>
            )}

            {/* Summary */}
            <div className="bg-muted/50 p-6 rounded-md border border-border">
              <h2 className="text-lg font-semibold mb-3">
                {language === "ko"
                  ? "요약"
                  : language === "en"
                  ? "Summary"
                  : "요약 / Summary"}
              </h2>
              {language === "dual" ? (
                <div className="space-y-3">
                  <p className="text-base leading-relaxed text-primary">
                    {article.summary || article.summaryKo}
                  </p>
                  <p className="text-base leading-relaxed text-muted-foreground">
                    {article.summaryKo || article.summary}
                  </p>
                </div>
              ) : (
                <p className="text-base leading-relaxed">
                  {language === "ko"
                    ? article.summaryKo || article.summary
                    : article.summary || article.summaryKo}
                </p>
              )}
            </div>

            {article.sourceUrl && (
              <Alert>
                <ExternalLink className="h-4 w-4" />
                <AlertDescription className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <span>
                    이 기사는 표시된 원문을 바탕으로 OZK 뉴스 편집팀이 요약·번역했습니다.
                    중요한 결정 전에는 원문과 해당 기관의 최신 발표를 확인하세요.
                  </span>
                  <a
                    href={article.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="inline-flex shrink-0 items-center gap-1 font-medium text-primary underline-offset-4 hover:underline"
                    data-testid="link-article-source"
                  >
                    원문·출처 확인
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </AlertDescription>
              </Alert>
            )}

            {(article.createdBy === "bot" || article.createdBy === "bot-local") && (
              <p className="text-xs leading-relaxed text-muted-foreground">
                AI는 초안 작성과 번역을 보조하며, 발행 여부는 출처·날짜·본문 완성도 기준으로 검토됩니다.
              </p>
            )}

            {/* Content */}
            <div className="prose prose-lg max-w-none dark:prose-invert">
              {showSimple && simpleVersion ? (
                <div className="bg-muted/30 p-6 rounded-md border border-border">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">쉬운 영어 버전</h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowSimple(false)}
                    >
                      원문 보기
                    </Button>
                  </div>
                  {/* AI 생성 콘텐츠 고지 */}
                  <Alert className="mb-4">
                    <Brain className="h-4 w-4" />
                    <AlertDescription>
                      이 콘텐츠는 AI가 보조하여 작성되었습니다. 정확성을 보장하지 않으며, 참고용으로만 사용하세요.
                    </AlertDescription>
                  </Alert>
                  <h2 className="text-2xl font-bold mb-4">
                    {simpleVersion.simpleTitle}
                  </h2>
                  <div className="whitespace-pre-wrap leading-relaxed">
                    {simpleVersion.simpleContent}
                  </div>
                </div>
              ) : language === "dual" ? (
                <div data-testid="article-content">{renderDualContent()}</div>
              ) : (
                <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
                  <PopoverTrigger asChild>
                    <div
                      ref={contentRef}
                      className="whitespace-pre-wrap leading-relaxed select-text cursor-text"
                      onDoubleClick={handleWordSelection}
                      onClick={handleWordClick}
                      data-testid="article-content"
                    >
                      {language === "ko"
                        ? article.contentKo || article.content
                        : article.content || article.contentKo}
                    </div>
                  </PopoverTrigger>
                  <PopoverContent className="w-80">
                    {isLoadingDefinition ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="h-6 w-6 animate-spin" />
                      </div>
                    ) : wordDefinition ? (
                      <div className="space-y-3">
                        <div>
                          <h4 className="font-bold text-lg">{selectedWord}</h4>
                          {wordDefinition.pronunciation && (
                            <p className="text-sm text-muted-foreground">
                              {wordDefinition.pronunciation}
                            </p>
                          )}
                        </div>
                        <div>
                          <p className="font-semibold text-sm">뜻:</p>
                          <p className="text-sm">{wordDefinition.definition}</p>
                          {wordDefinition.korean && (
                            <p className="text-sm text-muted-foreground mt-1">
                              ({wordDefinition.korean})
                            </p>
                          )}
                        </div>
                        {wordDefinition.example && (
                          <div>
                            <p className="font-semibold text-sm">예문:</p>
                            <p className="text-sm italic">
                              {wordDefinition.example}
                            </p>
                          </div>
                        )}
                      </div>
                    ) : null}
                  </PopoverContent>
                </Popover>
              )}
            </div>

            {/* Ad 2: Mid-Article Inline (Guest/Free/Premium only) */}
            {userProfile?.role !== "platinum" && (
              <div data-testid="ad-banner-inline">
                <AdBanner size="inline" tags={article?.tags ?? undefined} />
              </div>
            )}

            {/* Quiz Section */}
            {showQuiz && quiz && quiz.questions && (
              <div className="bg-muted/30 p-6 rounded-md border border-border space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold">영어 학습 퀴즈</h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowQuiz(false)}
                  >
                    닫기
                  </Button>
                </div>
                {/* AI 생성 콘텐츠 고지 */}
                <Alert className="mb-4">
                  <Brain className="h-4 w-4" />
                  <AlertDescription>
                    이 퀴즈는 AI가 생성했습니다. 정확성을 보장하지 않으며, 학습 보조용으로만 사용하세요.
                  </AlertDescription>
                </Alert>
                {quiz.questions.map((q: any, i: number) => (
                  <div key={i} className="space-y-3">
                    <p className="font-semibold">
                      {i + 1}. {q.question}
                    </p>
                    <div className="space-y-2 pl-4">
                      {q.options.map((option: string, optIdx: number) => {
                        const optionLetter = String.fromCharCode(65 + optIdx);
                        const isSelected = quizAnswers[i] === optionLetter;
                        const isCorrect = q.correct === optionLetter;
                        const showResult = showQuizResults;

                        return (
                          <label
                            key={optIdx}
                            className={`flex items-center gap-2 p-2 rounded-md cursor-pointer hover-elevate ${
                              showResult
                                ? isCorrect
                                  ? "bg-green-500/20 border border-green-500"
                                  : isSelected
                                  ? "bg-red-500/20 border border-red-500"
                                  : ""
                                : isSelected
                                ? "bg-primary/10"
                                : ""
                            }`}
                          >
                            <input
                              type="radio"
                              name={`question-${i}`}
                              value={optionLetter}
                              checked={isSelected}
                              onChange={() => {
                                const newAnswers = [...quizAnswers];
                                newAnswers[i] = optionLetter;
                                setQuizAnswers(newAnswers);
                              }}
                              disabled={showQuizResults}
                              className="cursor-pointer"
                            />
                            <span>
                              {optionLetter}) {option}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                    {showQuizResults && q.explanation && (
                      <div className="pl-4 pt-2 text-sm text-muted-foreground border-l-2 border-primary">
                        💡 {q.explanation}
                      </div>
                    )}
                  </div>
                ))}
                {!showQuizResults && (
                  <Button onClick={submitQuiz} className="w-full">
                    정답 확인
                  </Button>
                )}
              </div>
            )}

            {/* Learning Tip */}
            {language === "en" && !showSimple && !showQuiz && (
              <div className="bg-primary/5 border border-primary/20 p-4 rounded-md">
                <p className="text-sm text-muted-foreground">
                  💡 <strong>학습 팁:</strong> 영어 단어를 클릭하면 뜻을 볼 수
                  있고, 더블클릭하면 단어장에 저장할 수 있습니다.
                </p>
              </div>
            )}
            {language === "dual" && (
              <div className="bg-primary/5 border border-primary/20 p-4 rounded-md">
                <p className="text-sm text-muted-foreground">
                  💡 <strong>학습 팁:</strong> 한 문단씩 영어와 한글이 동시에
                  표시됩니다. 영어를 먼저 읽고 이해한 후, 한글로 확인하세요.
                </p>
              </div>
            )}

            {/* Article End - Author & Bookmark */}
            {article.author && (
              <div className="mt-8 pt-6 border-t border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <User className="h-5 w-5" />
                    <span className="text-base font-medium">
                      {article.author} 기자
                    </span>
                  </div>
                  {isAuthenticated && (
                    <Button
                      variant={isBookmarked ? "default" : "outline"}
                      onClick={handleBookmark}
                      disabled={isBookmarking}
                      className="gap-2"
                      data-testid="button-bookmark-bottom"
                    >
                      {isBookmarking ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : isBookmarked ? (
                        <BookmarkCheck className="h-5 w-5" />
                      ) : (
                        <Bookmark className="h-5 w-5" />
                      )}
                      <span>{isBookmarked ? "찜 완료" : "찜하기"}</span>
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* 편집자 주 - AI 작성 고지 */}
            <div className="mt-8 pt-6 border-t border-border">
              <div className="space-y-1">
                <p className="text-[10px] font-medium text-muted-foreground mb-1">📘 편집자 주</p>
                <p className="text-[10px] text-muted-foreground leading-tight">
                  본 기사는 OZK NEWS 기자가 AI Assistant의 협업을 통해 작성한 기사입니다.
                </p>
                <p className="text-[10px] text-muted-foreground leading-tight">
                  사용된 이미지는 직접 촬영 또는 저작권 문제가 없는 자료를 활용하였습니다.
                </p>
                <p className="text-[10px] text-muted-foreground leading-tight">
                  기사 내용 중 오류나 저작권 문제가 발견될 경우 신속히 수정 또는 삭제하겠습니다.
                </p>
              </div>
            </div>

            {/* Comments Section */}
            <div className="mt-12">
              <Comments articleId={article.id} />
            </div>
          </article>

          {/* Sidebar */}
          <aside className="space-y-6">
            {/* Related Articles */}
            {related.length > 0 && (
              <div className="space-y-4">
                <h2 className="text-xl font-bold">관련 기사</h2>
                <div className="space-y-4">
                  {related.map((relatedArticle) => (
                    <NewsCard
                      key={relatedArticle.id}
                      article={relatedArticle}
                    />
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>
      </PageContainer>

      <AuthDialog open={showAuthDialog} onOpenChange={setShowAuthDialog} />
    </>
  );
}
